import React, { useRef, useState } from 'react';
import { AnimatePresence, motion, useReducedMotion } from 'framer-motion';
import { ChevronLeftIcon, ChevronRightIcon, DownloadIcon, LightbulbIcon, RotateCcwIcon } from 'lucide-react';
import { featuredLesson } from '../../lib/appAssets';
type Step = {
  kind: 'card';
  label: string;
  copy: string;
  src: string;
  alt: string;
  width: number;
  height: number;
} | {
  kind: 'check';
  label: string;
} | {
  kind: 'reward';
  label: string;
};
const steps: Step[] = [{
  kind: 'card',
  ...featuredLesson.heroCard
}, ...featuredLesson.cards.map(card => ({
  kind: 'card' as const,
  ...card
})), {
  kind: 'check',
  label: 'Screen 14 of 15'
}, {
  kind: 'reward',
  label: 'Screen 15 of 15'
}];

/** The free in-browser sample: every screen, the question and the badge. */
export function LessonWalkthrough() {
  const [index, setIndex] = useState(0);
  const [revealed, setRevealed] = useState(false);
  const reduceMotion = useReducedMotion();
  const stripRef = useRef<HTMLDivElement>(null);
  const step = steps[index];
  const isLast = index === steps.length - 1;
  const progress = (index + 1) / steps.length * 100;
  const select = (next: number, focusTab = false) => {
    const clamped = Math.min(steps.length - 1, Math.max(0, next));
    setIndex(clamped);
    setRevealed(false);
    const tab = stripRef.current?.querySelectorAll<HTMLButtonElement>('[role="tab"]')[clamped];
    if (focusTab) tab?.focus();
    tab?.scrollIntoView({
      block: 'nearest',
      inline: 'nearest'
    });
  };
  const onStripKeyDown = (event: React.KeyboardEvent) => {
    const map: Record<string, number> = {
      ArrowRight: index + 1,
      ArrowLeft: index - 1,
      Home: 0,
      End: steps.length - 1
    };
    if (event.key in map) {
      event.preventDefault();
      select(map[event.key], true);
    }
  };
  const transition = reduceMotion ? {
    duration: 0
  } : {
    duration: 0.2,
    ease: [0.23, 1, 0.32, 1] as const
  };
  const fade = {
    initial: reduceMotion ? undefined : {
      opacity: 0,
      y: 8
    },
    animate: {
      opacity: 1,
      y: 0
    },
    exit: reduceMotion ? undefined : {
      opacity: 0,
      y: -8
    },
    transition
  };
  return <div id="lesson" className="scroll-mt-24 overflow-hidden rounded-4xl border-2 border-ink bg-white shadow-chunky" data-id="element-40" data-mp="102sv6uc1tyu">
      {/* Progress */}
      <div className="flex items-center gap-4 border-b-2 border-ink/10 px-5 py-4 md:px-8" data-id="element-41" data-mp="3ifhxobsh19eg">
        <p className="whitespace-nowrap text-[15px] font-bold text-ink" data-id="element-42" data-mp="3neunn5en2wl9">{step.label}</p>
        <div className="h-2.5 flex-1 overflow-hidden rounded-full bg-ink/10" role="progressbar" aria-label="Sample progress" aria-valuemin={1} aria-valuemax={steps.length} aria-valuenow={index + 1} data-id="element-43" data-mp="39fcteaikqr4i">
          <div className="h-full rounded-full bg-coral transition-[width] duration-200 ease-out" style={{
          width: `${progress}%`
        }} data-id="element-44" data-mp="34h1jjzsa4gma" />
        </div>
      </div>

      <div className="grid lg:grid-cols-[minmax(0,1fr)_minmax(0,1fr)]" data-id="element-45" data-mp="12qzq7wq00krw">
        <div className="bg-cream-field p-4 md:p-8" data-id="element-46" data-mp="2n8ifqk1gin5y">
          <AnimatePresence mode="wait" initial={false} data-id="element-47" data-mp="2n9ada0em6uyv">
            <motion.div key={index} {...fade} data-id="element-48" data-mp="2i9o01qysdeq2">
              <StepArt step={step} data-id="element-49" data-mp="2iaz3tqs8emh6" />
            </motion.div>
          </AnimatePresence>
        </div>

        <div id="lesson-stage" role="tabpanel" aria-live="polite" className="flex flex-col justify-between gap-8 p-6 md:p-10" data-id="element-50" data-mp="2wzv6f8bir7ae">
          <AnimatePresence mode="wait" initial={false} data-id="element-51" data-mp="35xyuocdw2i5w">
            <motion.div key={index} {...fade} data-id="element-52" data-mp="30zxcdtoaemvh">
              {step.kind === 'card' && <p className="font-display text-[24px] leading-snug text-ink md:text-[30px]" data-id="element-53" data-mp="3129ohq7d630m">{step.copy}</p>}

              {step.kind === 'check' && <div data-id="element-54" data-mp="3aymh0kr6aats">
                  <p className="text-[15px] font-bold text-teal" data-id="element-55" data-mp="3aw0ex0n1wniy">Quick question</p>
                  <p className="mt-2 font-display text-[24px] leading-snug text-ink md:text-[30px]" data-id="element-56" data-mp="1ljf0q6pro50q">
                    {featuredLesson.quickCheck.question}
                  </p>
                  {revealed ? <p className="mt-6 rounded-2xl border-2 border-teal bg-teal/10 px-5 py-4 text-[17px] font-semibold text-ink" data-id="element-57" data-mp="7p7xofgt4j5l">
                      {featuredLesson.quickCheck.reveal}
                    </p> : <button type="button" onClick={() => setRevealed(true)} className="mt-6 inline-flex items-center gap-2 rounded-2xl border-2 border-ink bg-sun px-6 py-3 font-display text-[18px] text-ink shadow-chunky-sm transition-transform duration-150 ease-out active:translate-y-0.5 active:shadow-none" data-id="element-58" data-mp="lneoxcr93kwa">
                      <LightbulbIcon className="h-5 w-5" aria-hidden="true" data-id="element-59" data-mp="2ok9s2nvkifl" />
                      Tap to reveal
                    </button>}
                  <p className="mt-4 text-[16px] text-ink-soft" data-id="element-60" data-mp="329fsaw07bqyg">Nothing is marked wrong, and there is no score.</p>
                </div>}

              {step.kind === 'reward' && <div data-id="element-61" data-mp="2yricfjdowxmv">
                  <p className="text-[15px] font-bold text-teal" data-id="element-62" data-mp="2tui4f2xa5kt4">Badge earned</p>
                  <p className="mt-2 font-display text-[28px] leading-snug text-ink md:text-[34px]" data-id="element-63" data-mp="2tsf9q5l6n3i2">
                    {featuredLesson.collectible.name}
                  </p>
                  <p className="mt-3 text-[17px] text-ink-soft" data-id="element-64" data-mp="2ktbkejvf9rv4">
                    {featuredLesson.answer} In the app, this badge is your child’s to keep and download.
                  </p>
                  <p className="mt-4 text-[16px] text-ink-soft" data-id="element-65" data-mp="2b38mq6dcvpqg">Try this at home: {featuredLesson.tryIt}</p>
                </div>}
            </motion.div>
          </AnimatePresence>

          <div className="flex flex-wrap items-center gap-3" data-id="element-66" data-mp="224n307w5e4o3">
            <button type="button" onClick={() => select(index - 1)} disabled={index === 0} className="inline-flex h-12 w-12 items-center justify-center rounded-2xl border-2 border-ink bg-white text-ink transition-colors duration-150 ease-out hover:bg-lavender-soft disabled:cursor-not-allowed disabled:opacity-35" aria-label="Previous card" data-id="element-67" data-mp="1x4segazuhr67">
              <ChevronLeftIcon className="h-5 w-5" aria-hidden="true" data-id="element-68" data-mp="18kfkzlftaaip" />
            </button>
            {isLast ? <button type="button" onClick={() => select(0)} className="inline-flex items-center gap-2 rounded-2xl border-2 border-ink bg-white px-6 py-3 font-display text-[17px] text-ink transition-colors duration-150 ease-out hover:bg-lavender-soft" data-id="element-69" data-mp="1diz6jkqhtkaj">
                <RotateCcwIcon className="h-5 w-5" aria-hidden="true" data-id="element-70" data-mp="1rg5nravkjaxn" />
                Start again
              </button> : <button type="button" onClick={() => select(index + 1)} className="inline-flex items-center gap-2 rounded-2xl border-2 border-ink bg-coral px-6 py-3 font-display text-[17px] text-ink shadow-chunky-sm transition-[transform,box-shadow] duration-150 ease-out active:translate-y-0.5 active:shadow-none" data-id="element-71" data-mp="2k6dxcec1gn0m">
                Next card
                <ChevronRightIcon className="h-5 w-5" aria-hidden="true" data-id="element-72" data-mp="n8q4af7dfrmx" />
              </button>}
          </div>
        </div>
      </div>

      {/* Every screen, jumpable. Buttons and rail are positioned so the
          hidden labels stay contained inside the scrolling rail. */}
      <div ref={stripRef} role="tablist" aria-label={`Every screen of ${featuredLesson.title}`} aria-orientation="horizontal" onKeyDown={onStripKeyDown} className="relative flex gap-2 overflow-x-auto border-t-2 border-ink/10 bg-white p-4" data-id="element-73" data-mp="3lqlwk6juf6bd">
        {steps.map((thumb, i) => {
        const active = i === index;
        return <button key={thumb.label} type="button" role="tab" aria-selected={active} aria-controls="lesson-stage" tabIndex={active ? 0 : -1} onClick={() => select(i)} className={`relative shrink-0 overflow-hidden rounded-xl border-2 transition-colors duration-150 ease-out ${active ? 'border-coral' : 'border-transparent hover:border-ink/25'}`} data-id="element-74" data-mp="1ayaxrs15i7ag" data-mpi={i}>
              <span className="sr-only" data-id="element-75" data-mp="3n7ratjbrwysz">{thumb.label}</span>
              <Thumb step={thumb} data-id="element-76" data-mp="3n9c4l6q9kdps" />
            </button>;
      })}
      </div>
    </div>;
}
function StepArt({
  step
}: {
  step: Step;
}) {
  const box = 'mx-auto aspect-square w-full max-w-[460px]';
  if (step.kind === 'card') {
    return <img src={step.src} alt={step.alt} loading="eager" width={step.width} height={step.height} className={`${box} object-contain`} data-id="element-77" data-mp="5ze0qig0c8xy" />;
  }
  if (step.kind === 'check') {
    const art = featuredLesson.cards[5];
    return <img src={art.src} alt={art.alt} loading="lazy" decoding="async" width={art.width} height={art.height} className={`${box} object-contain`} data-id="element-78" data-mp="ov4lfz9yl8lr" />;
  }
  const badge = featuredLesson.collectible;
  return <div className={`${box} relative`} data-id="element-79" data-mp="g2jf5inikudt">
      <img src={badge.src} alt={badge.alt} loading="lazy" decoding="async" width={badge.width} height={badge.height} className="h-full w-full object-contain" data-id="element-80" data-mp="g3kq7n4j5gvj" />
      <span aria-hidden="true" className="absolute bottom-3 right-3 inline-flex items-center gap-1.5 rounded-xl border-2 border-ink bg-coral px-3 py-1.5 text-[14px] font-bold text-ink" data-id="element-81" data-mp="17uqw6k6xm8sq">
        <DownloadIcon className="h-4 w-4" data-id="element-82" data-mp="ysrc1j0meolk" /> Save badge
      </span>
    </div>;
}
function Thumb({
  step
}: {
  step: Step;
}) {
  const size = 'h-14 w-14 bg-cream-field object-contain md:h-16 md:w-16';
  if (step.kind === 'card') {
    return <img src={step.src} alt="" loading="lazy" decoding="async" width={step.width} height={step.height} className={size} data-id="element-83" data-mp="3jl2yfvo7spwh" />;
  }
  if (step.kind === 'check') {
    return <span className={`flex items-center justify-center ${size}`} data-id="element-84" data-mp="3mq44c4ihm6y3">
        <LightbulbIcon className="h-6 w-6 text-teal" strokeWidth={2.2} aria-hidden="true" data-id="element-85" data-mp="3hq82obx9irlp" />
      </span>;
  }
  const badge = featuredLesson.collectible;
  return <img src={badge.src} alt="" loading="lazy" decoding="async" width={badge.width} height={badge.height} className={size} data-id="element-86" data-mp="asx34rav851" />;
}